import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { renderMarkdown } from '../../../common/components/markdown/markdown';
import SearchBox from '../../../common/components/search-box/search-box';
import { METHODS, filterOperations, groupByTag, groupOf, readOperations, tagLabel } from '../utils/openapi';

import '../styles/openapi-viewer.css';

// An OpenAPI description, drawn as a reference.
//
// It receives the document already parsed and fetches nothing: which document,
// of which project, is the API reference tab's business.
//
// A READER, not a console. It sends no request to the project's API.
//
// THE FILTERS LIVE IN THE URL — `?q=`, `?method=`, `?tag=` — and an operation
// is linkable by its id in the hash, so "look at this route" is a link somebody
// can send.

export default function OpenApiViewer({ spec }) {
    const [params, setParams] = useSearchParams();
    const { hash } = useLocation();

    const operations = useMemo(() => readOperations(spec), [spec]);

    const q = params.get('q') || '';
    const method = params.get('method') || '';
    const tag = params.get('tag') || '';
    const openId = decodeURIComponent(hash.replace(/^#/, ''));

    const filtered = useMemo(
        () => filterOperations(operations, { q, method, tag }),
        [operations, q, method, tag],
    );
    const groups = useMemo(() => groupByTag(spec, filtered), [spec, filtered]);
    const allTags = useMemo(() => groupByTag(spec, operations), [spec, operations]);

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        setParams(target, { replace: true });
    };

    const clearFilters = () => {
        const target = new URLSearchParams(params);
        ['q', 'method', 'tag'].forEach(key => target.delete(key));
        setParams(target, { replace: true });
    };

    const hasFilters = !!(q || method || tag);
    const base = spec?.servers?.[0]?.url;

    return (
        <section className="api-ref">
            <dl className="api-ref__meta">
                <div>
                    <dt>API</dt>
                    <dd>{spec?.info?.title ?? '—'}</dd>
                </div>
                <div>
                    <dt>Versión</dt>
                    <dd>{spec?.info?.version ?? '—'} · OpenAPI {spec?.openapi ?? spec?.swagger ?? '—'}</dd>
                </div>
                <div>
                    <dt>Operaciones</dt>
                    <dd>{operations.length}</dd>
                </div>
                {base && (
                    <div>
                        <dt>Base</dt>
                        <dd><code>{base}</code></dd>
                    </div>
                )}
            </dl>

            {spec?.info?.description && (
                <div className="md md--field">{renderMarkdown(spec.info.description)}</div>
            )}

            <div className="api-ref__toolbar">
                <SearchBox placeholder="Buscar por ruta, resumen o permiso" />

                <div className="api-ref__methods" role="group" aria-label="Filtrar por método">
                    <button
                        type="button"
                        className={`api-ref__method-filter ${!method ? 'api-ref__method-filter--on' : ''}`}
                        onClick={() => setFilter('method', '')}
                        aria-pressed={!method}
                    >
                        Todos
                    </button>
                    {METHODS.map(item => (
                        <button
                            key={item}
                            type="button"
                            className={`api-ref__method-filter api-ref__method-filter--${item} ${method === item ? 'api-ref__method-filter--on' : ''}`}
                            onClick={() => setFilter('method', method === item ? '' : item)}
                            aria-pressed={method === item}
                        >
                            {item.toUpperCase()}
                        </button>
                    ))}
                </div>

                {hasFilters && (
                    <button type="button" className="api-ref__clear" onClick={clearFilters}>
                        Limpiar
                    </button>
                )}
            </div>

            <div className="api-ref__layout">
                <nav className="api-ref__nav" aria-label="Grupos de la API">
                    <button
                        type="button"
                        className={`api-ref__nav-item ${!tag ? 'api-ref__nav-item--on' : ''}`}
                        onClick={() => setFilter('tag', '')}
                    >
                        <span>Todos</span>
                        <span className="api-ref__count">{operations.length}</span>
                    </button>
                    {allTags.map((group, index) => {
                        const heading = groupOf(group.tag);
                        const showHeading = index === 0 || groupOf(allTags[index - 1].tag) !== heading;
                        return (
                            <div key={group.tag}>
                                {showHeading && heading !== group.tag && (
                                    <span className="api-ref__nav-group">{heading}</span>
                                )}
                                <button
                                    type="button"
                                    className={`api-ref__nav-item ${tag === group.tag ? 'api-ref__nav-item--on' : ''}`}
                                    onClick={() => setFilter('tag', tag === group.tag ? '' : group.tag)}
                                >
                                    <span>{tagLabel(group.tag)}</span>
                                    <span className="api-ref__count">{group.operations.length}</span>
                                </button>
                            </div>
                        );
                    })}
                </nav>

                <div className="api-ref__content">
                    {!groups.length && (
                        <p className="api-ref__state">
                            {operations.length
                                ? 'Ninguna ruta coincide con estos filtros.'
                                : 'El documento es un OpenAPI válido, pero no declara ninguna ruta.'}
                        </p>
                    )}

                    {groups.map(group => (
                        <section key={group.tag} className="api-ref__group">
                            <h3 className="api-ref__group-title">
                                {group.tag}
                                <span className="api-ref__count">{group.operations.length}</span>
                            </h3>
                            <ul className="api-ref__operations">
                                {group.operations.map(operation => (
                                    <Operation
                                        key={operation.id}
                                        operation={operation}
                                        open={openId === operation.id}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Closed by default and opened on demand: two hundred expanded operations is a
// wall, not a reference. The <details> is native, so it opens with the
// keyboard and costs no state.
function Operation({ operation, open }) {
    return (
        <li className="api-op" id={operation.id}>
            <details className="api-op__details" open={open}>
                <summary className="api-op__summary">
                    <span className={`api-op__method api-op__method--${operation.method}`}>
                        {operation.method.toUpperCase()}
                    </span>
                    <code className="api-op__path">{operation.short}</code>
                    <span className="api-op__summary-text">{operation.summary}</span>
                    {operation.permission && (
                        <code className="api-op__permission" title="Permiso">{operation.permission}</code>
                    )}
                </summary>

                <div className="api-op__body">
                    <p className="api-op__full-path">
                        <code>{operation.method.toUpperCase()} {operation.path}</code>
                        <a href={`#${operation.id}`} className="api-op__anchor" title="Enlace a esta ruta">#</a>
                    </p>

                    {operation.description && (
                        <div className="md md--field">{renderMarkdown(operation.description)}</div>
                    )}

                    <dl className="api-op__facts">
                        <div>
                            <dt>Permiso</dt>
                            <dd>{operation.permission ? <code>{operation.permission}</code> : 'no declarado'}</dd>
                        </div>
                        <div>
                            <dt>Credenciales</dt>
                            <dd>{operation.credentials.length ? operation.credentials.join(' o ') : 'ninguna'}</dd>
                        </div>
                    </dl>

                    {operation.parameters.length > 0 && (
                        <Table
                            title="Parámetros"
                            head={['Nombre', 'En', 'Req.', 'Tipo']}
                            rows={operation.parameters.map(parameter => [
                                <code key="n">{parameter.name}</code>,
                                parameter.in,
                                parameter.required ? 'sí' : '—',
                                parameter.type,
                            ])}
                        />
                    )}

                    {operation.body && (
                        <>
                            <Table
                                title="Cuerpo (application/json)"
                                head={['Campo', 'Tipo', 'Req.', 'Reglas', 'Descripción']}
                                rows={operation.body.map(field => [
                                    <code key="n">{field.name}</code>,
                                    field.type,
                                    field.required ? 'sí' : '—',
                                    field.rules ? <Inline key="r" text={field.rules} /> : '—',
                                    field.description || '—',
                                ])}
                            />
                            {operation.bodyExample != null && (
                                <pre className="api-op__example">
                                    <code>{JSON.stringify(operation.bodyExample, null, 2)}</code>
                                </pre>
                            )}
                        </>
                    )}

                    {operation.responses.length > 0 && (
                        <div className="api-op__section">
                            <h4 className="api-op__section-title">Respuestas</h4>
                            <ul className="api-op__responses">
                                {operation.responses.map(response => (
                                    <li key={response.code}>
                                        <code className={`api-op__code api-op__code--${response.code[0]}xx`}>
                                            {response.code}
                                        </code>
                                        {' '}{response.description}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </details>
        </li>
    );
}

// The rules line carries `backticks`; the renderer turns them into code.
function Inline({ text }) {
    const blocks = renderMarkdown(text);
    return <span className="api-op__inline">{blocks?.[0]?.props?.children ?? text}</span>;
}

function Table({ title, head, rows }) {
    return (
        <div className="api-op__section">
            <h4 className="api-op__section-title">{title}</h4>
            <div className="api-op__table-wrap">
                <table className="api-op__table">
                    <thead>
                        <tr>{head.map(cell => <th key={cell}>{cell}</th>)}</tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index}>
                                {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
